/**
 * useAuth hook - access authenticated session state and actions.
 *
 * Must be called from inside an AuthProvider. Throws a descriptive error
 * if invoked outside the provider tree (catches missing-wrapper bugs early).
 *
 * @returns AuthContextValue with user, status, login, and logout
 * @throws Error when called outside an AuthProvider
 */
import { useContext } from 'react';
import { AuthContext } from './AuthProvider';
import type { AuthContextValue } from './schemas';

/**
 * Returns the current auth context value.
 *
 * @example
 * const { user, status, login, logout } = useAuth();
 * if (status === 'authenticated') {
 *   // user is guaranteed non-null here
 * }
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);

  if (ctx === null) {
    throw new Error(
      'useAuth() must be called inside an <AuthProvider>. ' +
        'Wrap your component tree with <AuthProvider> in main.tsx.',
    );
  }

  return ctx;
}
